// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import styles from '@app/features/auth/state/NewDeviceMonitoring.module.css';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {VoiceDeviceState} from '@app/features/voice/utils/VoiceDeviceManager';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans} from '@lingui/react/macro';
import {makeAutoObservable, runInAction} from 'mobx';
import type React from 'react';

const logger = new Logger('NewDeviceMonitoring');

type DeviceType = 'input' | 'output';
interface PendingDevicePrompt {
	deviceId: string;
	deviceName: string;
	deviceType: DeviceType;
}

interface IgnoreDeviceLinkProps {
	deviceName: string;
	onClick: () => void;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
}

const IgnoreDeviceLink: React.FC<IgnoreDeviceLinkProps> = ({deviceName, onClick}) => (
	<button type="button" className={styles.ignoreDeviceLink} onClick={onClick}>
		<Trans>Don't suggest {deviceName} again</Trans>
	</button>
);

class NewDeviceMonitoring {
	knownDeviceIds: Array<string> = [];
	ignoredDeviceIds: Array<string> = [];
	suppressAlerts = false;
	private isInitialized = false;
	private isStarted = false;
	private startPromise: Promise<void> | null = null;
	private startEpoch = 0;
	private pendingPrompts: Array<PendingDevicePrompt> = [];
	private isShowingPrompt = false;
	private unsubscribe: (() => void) | null = null;
	private i18n: I18n | null = null;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	setI18n(i18n: I18n): void {
		this.i18n = i18n;
	}
	private startMonitoring(): void {
		if (this.unsubscribe) return;
		this.unsubscribe = VoiceDevicePermissionState.subscribe(this.handleDeviceStateChange);
	}
	async start(): Promise<void> {
		if (this.startPromise) return this.startPromise;
		this.isStarted = true;
		const epoch = ++this.startEpoch;
		this.startPromise = (async () => {
			await makePersistent(this, 'NewDeviceMonitoring', ['knownDeviceIds', 'ignoredDeviceIds', 'suppressAlerts']);
			if (!this.isStarted || epoch !== this.startEpoch) return;
			this.startMonitoring();
		})();
		return this.startPromise;
	}
	private handleDeviceStateChange(state: VoiceDeviceState): void {
		if (!this.isStarted) return;
		if (state.permissionStatus !== 'granted') {
			return;
		}
		if (this.suppressAlerts) {
			return;
		}
		const currentInputIds = state.inputDevices.map((d) => d.deviceId);
		const currentOutputIds = state.outputDevices.map((d) => d.deviceId);
		const allCurrentIds = [...currentInputIds, ...currentOutputIds];
		if (!this.isInitialized) {
			runInAction(() => {
				this.knownDeviceIds = [...new Set([...this.knownDeviceIds, ...allCurrentIds])];
				this.isInitialized = true;
			});
			logger.debug('Initialized with known devices', {count: this.knownDeviceIds.length});
			return;
		}
		const newInputDevices = state.inputDevices.filter(
			(device) =>
				device.deviceId !== 'default' &&
				!this.knownDeviceIds.includes(device.deviceId) &&
				!this.ignoredDeviceIds.includes(device.deviceId) &&
				device.label,
		);
		const newOutputDevices = state.outputDevices.filter(
			(device) =>
				device.deviceId !== 'default' &&
				!this.knownDeviceIds.includes(device.deviceId) &&
				!this.ignoredDeviceIds.includes(device.deviceId) &&
				device.label,
		);
		if (newInputDevices.length > 0 || newOutputDevices.length > 0) {
			logger.debug('New devices detected', {
				inputs: newInputDevices.map((d) => d.label),
				outputs: newOutputDevices.map((d) => d.label),
			});
			runInAction(() => {
				for (const device of newInputDevices) {
					this.pendingPrompts.push({
						deviceId: device.deviceId,
						deviceName: device.label,
						deviceType: 'input',
					});
					this.knownDeviceIds.push(device.deviceId);
				}
				for (const device of newOutputDevices) {
					this.pendingPrompts.push({
						deviceId: device.deviceId,
						deviceName: device.label,
						deviceType: 'output',
					});
					this.knownDeviceIds.push(device.deviceId);
				}
			});
			this.processNextPrompt();
		}
	}
	private processNextPrompt(): void {
		if (!this.isStarted) return;
		if (this.isShowingPrompt || this.pendingPrompts.length === 0) {
			return;
		}
		const prompt = this.pendingPrompts.shift();
		if (!prompt) {
			return;
		}
		this.isShowingPrompt = true;
		this.showNewDeviceModal(prompt);
	}
	private showNewDeviceModal(prompt: PendingDevicePrompt): void {
		if (!this.i18n) {
			throw new Error('NewDeviceMonitoring: i18n not initialized');
		}
		const i18n = this.i18n;
		const {deviceId, deviceName, deviceType} = prompt;
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(msg`New audio device detected!`)}
					description={
						deviceType === 'input' ? (
							<Trans>
								Fluxer has found a new audio input device named <strong>{deviceName}</strong>. Do you want to switch to
								it?
							</Trans>
						) : (
							<Trans>
								Fluxer has found a new audio output device named <strong>{deviceName}</strong>. Do you want to switch to
								it?
							</Trans>
						)
					}
					primaryText={i18n._(msg`Switch Device`)}
					primaryVariant="primary"
					secondaryText={i18n._(msg`Not Now`)}
					checkboxContent={
						<IgnoreDeviceLink
							deviceName={deviceName}
							onClick={() => {
								this.addToIgnored(deviceId);
								ModalCommands.pop();
								setTimeout(() => this.onModalClosed(), 0);
							}}
						/>
					}
					onPrimary={(dontAskAgain) => {
						if (deviceType === 'input') {
							VoiceSettings.updateSettings({inputDeviceId: deviceId});
						} else {
							VoiceSettings.updateSettings({outputDeviceId: deviceId});
						}
						if (dontAskAgain) {
							this.addToIgnored(deviceId);
						}
						setTimeout(() => this.onModalClosed(), 0);
					}}
					onSecondary={(dontAskAgain) => {
						if (dontAskAgain) {
							this.addToIgnored(deviceId);
						}
						setTimeout(() => this.onModalClosed(), 0);
					}}
				/>
			)),
		);
	}
	private onModalClosed(): void {
		this.isShowingPrompt = false;
		this.processNextPrompt();
	}
	private addToIgnored(deviceId: string): void {
		if (!this.ignoredDeviceIds.includes(deviceId)) {
			runInAction(() => {
				this.ignoredDeviceIds.push(deviceId);
			});
			logger.debug('Added device to ignore list', {deviceId});
		}
	}
	clearIgnoredDevices(): void {
		this.ignoredDeviceIds = [];
		logger.debug('Cleared all ignored devices');
	}
	removeFromIgnored(deviceId: string): void {
		const index = this.ignoredDeviceIds.indexOf(deviceId);
		if (index !== -1) {
			this.ignoredDeviceIds.splice(index, 1);
			logger.debug('Removed device from ignore list', {deviceId});
		}
	}
	getIgnoredDeviceIds(): ReadonlyArray<string> {
		return this.ignoredDeviceIds;
	}
	setSuppressAlerts(suppress: boolean): void {
		this.suppressAlerts = suppress;
		logger.debug('Suppress alerts setting changed', {suppress});
	}
	showTestModal(): void {
		this.showNewDeviceModal({
			deviceId: 'test-device-id',
			deviceName: 'Test Audio Device',
			deviceType: 'input',
		});
	}
	dispose(): void {
		this.isStarted = false;
		this.startPromise = null;
		this.startEpoch++;
		this.pendingPrompts = [];
		this.isShowingPrompt = false;
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
		this.isInitialized = false;
	}
}

export default new NewDeviceMonitoring();
